import {
    getAllDealsService,
    getAllLenderDealsService,
    getSpecificLenderDealsService,
    FetchDealsPayload,
    FetchLenderDealsPayload,
} from '../../../../../store/investor/service';
import { DEAL_VIEW_TYPES } from '../../../../../store/investor/constants';
import { USER_TYPE } from '../../../../../utils/constants/user';
import { SortConfig } from '../../../../../utils/constants/commonConstants';
import { JAVA_API_STATUS, SOMETHING_WRONG } from '../../../../../utils/axios-interceptor';
import { EXPORT_ROW_CAP } from '../../../../../common/_custom/DataTable/utils/dataTableExportUtils';
import { buildDealsBaseParams, buildDealsFilterPayload, getDealReceiverId } from './useDealsList';

const isDealsErrorResponse = (response: any): boolean => {
    const responseBodyData = response?.data?.responseBody?.responseData;
    const responseData = response?.data?.responseData;
    return (
        responseBodyData?.responseCode === JAVA_API_STATUS.ERROR || (responseData && responseData.responseCode !== JAVA_API_STATUS.SUCCESS)
    );
};

export type DealsExportContext = {
    userType: string;
    userData: any;
    platformData: { investorOrgId?: string } | null | undefined;
    selectedCategory: string;
    selectedLenderId: string | null;
    selectedOrgId: string | null;
    companyDashboardId?: string;
    appliedFilters: Record<string, any>;
    sortConfig: SortConfig;
};

export type FetchAllDealsForExportResult = {
    rows: any[];
    /** True when the EXPORT_ROW_CAP safety cap was hit before the last page — the export may be incomplete. */
    truncated: boolean;
};

// Rows fetched per page while paging the on-screen deals endpoints for export.
// Kept at the same page size the on-screen Deals table uses (50), NOT a larger
// bulk page: getAllDeals (Mandate View) enriches every returned deal server-side
// (per-deal investor-deal preparation, org-name maps, requirement counts), so a
// 500-row page enriching a whole org's deals in one request times out the client
// (observed on Mandate View — RECUR-26702). Sequential paging still fetches the
// full set; each request just stays as light as the on-screen fetch.
const EXPORT_PAGE_SIZE = 50;

/**
 * Resolves a page-fetcher function for the current view (category, role, filters,
 * search, sort), replicating the EXACT endpoint-selection + payload-building logic
 * useDealsList.ts's on-screen fetch effect uses for the current userType/selectedCategory
 * combination (see that effect — keep in sync):
 *   - COMPANY + DEAL_VIEW           -> getAllLenderDealsService
 *   - COMPANY + MANDATE_VIEW        -> getAllDealsService
 *   - LENDER/LENDER_TEAM user       -> getSpecificLenderDealsService
 *   - other non-company + DEAL_VIEW -> getAllLenderDealsService
 *   - other non-company + MANDATE_VIEW -> getAllDealsService
 * Returns null when the context can't resolve a valid request (e.g. COMPANY user
 * missing assesseeOrgId), matching the on-screen effect's own no-op guards.
 */
const resolveDealsExportFetcher = (ctx: DealsExportContext): ((pageNumber: number) => Promise<any>) | null => {
    const {
        userType,
        userData,
        platformData,
        selectedCategory,
        selectedLenderId,
        selectedOrgId,
        companyDashboardId,
        appliedFilters,
        sortConfig,
    } = ctx;

    const baseParamsForPage = (pageNumber: number, additionalParams: Record<string, any> = {}) =>
        buildDealsBaseParams({
            dealProposerId: platformData?.investorOrgId as string,
            pageNumber,
            pageSize: EXPORT_PAGE_SIZE,
            sortConfig,
            additionalParams,
        });

    if (userType === USER_TYPE.COMPANY) {
        const assesseeId = userData?.assesseeOrgId;
        if (!assesseeId) return null;

        if (selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW) {
            return (pageNumber: number) =>
                getAllLenderDealsService({
                    ...baseParamsForPage(pageNumber, { dealBeneficiaryId: assesseeId }),
                    filters: buildDealsFilterPayload(appliedFilters, {
                        beneficiaryIds: [assesseeId],
                        receivers: appliedFilters?.lenderName || [],
                    }),
                } as FetchLenderDealsPayload);
        }
        if (selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW) {
            return (pageNumber: number) =>
                getAllDealsService({
                    ...baseParamsForPage(pageNumber, { dealBeneficiaryId: assesseeId }),
                    dealStatuses: [],
                    filters: buildDealsFilterPayload(appliedFilters, {
                        beneficiaryIds: [assesseeId],
                        receivers: selectedLenderId ? [selectedLenderId] : [],
                        transformParams: ['customFields.', 'owners.'],
                    }),
                } as FetchDealsPayload);
        }
        return null;
    }

    if (userType === USER_TYPE.LENDER || userType === USER_TYPE.LENDER_TEAM || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW) {
        const isLenderUser = [USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM].includes(userType);
        const orgIdForFilter = selectedOrgId || companyDashboardId ? [(selectedOrgId || companyDashboardId) as string] : [];
        const dealReceiverId = getDealReceiverId(userType, selectedLenderId);
        const filters = buildDealsFilterPayload(appliedFilters, {
            beneficiaryIds: orgIdForFilter,
            receivers: [...(appliedFilters?.lenderName || []), ...(dealReceiverId ? [dealReceiverId] : [])],
        });

        return (pageNumber: number) => {
            const payload = { ...baseParamsForPage(pageNumber), filters } as FetchLenderDealsPayload;
            return isLenderUser ? getSpecificLenderDealsService(payload) : getAllLenderDealsService(payload);
        };
    }

    if (selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW) {
        const orgIdForFilter = selectedOrgId || companyDashboardId ? [(selectedOrgId || companyDashboardId) as string] : [];
        return (pageNumber: number) =>
            getAllDealsService({
                ...baseParamsForPage(pageNumber),
                dealStatuses: [],
                filters: buildDealsFilterPayload(appliedFilters, {
                    beneficiaryIds: orgIdForFilter,
                    receivers: selectedLenderId ? [selectedLenderId] : [],
                    transformParams: ['customFields.', 'owners.'],
                }),
            } as FetchDealsPayload);
    }

    return null;
};

/**
 * Mandate View rows are top-level deals, each carrying the expandable child
 * `investorDeals` shown when a mandate row is expanded on screen. Flatten each
 * deal into [deal, ...investorDeals] so the export mirrors the expanded on-screen
 * view (parent + its investor sub-rows), not just the parent rows. Deal View /
 * lender rows are already flat investor-deal rows and are returned unchanged.
 */
const flattenMandateRows = (rows: any[], selectedCategory: string): any[] => {
    if (selectedCategory !== DEAL_VIEW_TYPES.MANDATE_VIEW) return rows;
    // Tag child investor-deals with _isCollapsibleRow — the same flag the on-screen table sets
    // on expanded sub-rows — so per-field export getters can render them exactly like the
    // expanded view (e.g. Company Name shows the child's lenderName, not the parent borrowerName).
    return rows.flatMap((deal) => [
        deal,
        ...(Array.isArray(deal?.investorDeals) ? deal.investorDeals.map((inv: any) => ({ ...inv, _isCollapsibleRow: true })) : []),
    ]);
};

/**
 * Fetches every deal row matching the current view (category, filters, search,
 * sort) by sequentially paging the SAME on-screen deals endpoint (selected exactly
 * as useDealsList.ts does) in EXPORT_PAGE_SIZE-row chunks. Pages are awaited
 * sequentially (never in parallel) — concurrent paged calls previously cross-talked
 * on the backend's request correlation (see getUniqueRequestId's comment in service.ts).
 *
 * Mandate View: each fetched deal is flattened into its expandable child
 * `investorDeals` (parent + sub-rows) via flattenMandateRows, so the sheet matches
 * the on-screen expanded view.
 */
export const fetchAllDealsForExport = async (ctx: DealsExportContext): Promise<FetchAllDealsForExportResult> => {
    const fetchPage = resolveDealsExportFetcher(ctx);
    if (!fetchPage) return { rows: [], truncated: false };

    let rows: any[] = [];
    let pageNumber = 1;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const response = await fetchPage(pageNumber);

        if (isDealsErrorResponse(response)) {
            const responseBodyData = response?.data?.responseBody?.responseData;
            const responseData = response?.data?.responseData;
            throw new Error(responseBodyData?.responseMessage || responseData?.responseMessage || SOMETHING_WRONG);
        }

        const body = response?.data?.responseBody ?? {};
        const pageRows = body.data ?? [];
        rows = rows.concat(pageRows);

        const totalDeals = body?.dealCount?.totalDeals;
        const hitCap = rows.length >= EXPORT_ROW_CAP;
        // dealCount.totalDeals, when present, is the authoritative stop signal; falls
        // back to the page-shorter-than-pageSize heuristic when the backend omits it.
        const noMorePages = pageRows.length < EXPORT_PAGE_SIZE || (typeof totalDeals === 'number' && rows.length >= totalDeals);

        if (hitCap || noMorePages) {
            return { rows: flattenMandateRows(rows.slice(0, EXPORT_ROW_CAP), ctx.selectedCategory), truncated: hitCap && !noMorePages };
        }

        pageNumber += 1;
    }
};
